import { createElement, useCallback, useMemo } from 'react'
import { ChevronRight } from 'lucide-react'
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router'
import { BreadcrumbSubPagesDropdown } from '../components/BreadcrumbSubPagesDropdown'
import { CollapsibleAncestorBreadcrumbs } from '../components/CollapsibleAncestorBreadcrumbs'
import { PageDetailsView } from '../components/PageDetailsView'
import type { PageDocument } from '@/lib/db/rxdb/hooks/entities/pages'
import { usePages, useProjects } from '@/lib/db/rxdb/hooks'
import useLoginStore from '@/store/LoginStore'
import { Loading } from '@/components/app/indicators/Loading'
import TaskSidebar from '@/components/app/sidebars/task'
import { ProjectIcon } from '@/lib/utils/projectIcon'
import { StarToggle } from '@/components/app/StarToggle'

export const Route = createFileRoute(
  '/_AppWrapper/_AppLayout/$workspaceSlug/projects/$projectKey/pages/$pageSlug',
)({
  component: PageDetailsPage,
})

export default function PageDetailsPage() {
  const { workspaceSlug, projectKey, pageSlug } = Route.useParams()
  const navigate = useNavigate()
  const workspaceId = useLoginStore((state) => state.workspaceId)
  const userId = useLoginStore((state) => state.userId)

  const { projects } = useProjects()
  const { pages, isLoading: pagesLoading } = usePages()

  const project = useMemo(() => {
    return projects.find((p) => {
      if (p.isDeleted) return false
      if (p.projectKey.toLowerCase() !== projectKey.toLowerCase()) return false
      if (workspaceId && p.envId !== workspaceId) return false
      return true
    }) ?? null
  }, [projects, projectKey, workspaceId])

  const page = useMemo<PageDocument | null>(() => {
    if (!project) return null
    return pages.find((p) => {
      if (p.isDeleted) return false
      if (p.slug !== pageSlug) return false
      if (p.projectId !== project.id) return false
      return true
    }) ?? null
  }, [pages, pageSlug, project])

  const ancestors = useMemo(() => {
    if (!page || !project) return []
    const chain: Array<PageDocument> = []
    let current = page
    while (current.parentPageId) {
      const parent = pages.find((p) => p.id === current.parentPageId && !p.isDeleted)
      if (!parent) break
      chain.unshift(parent)
      current = parent
    }
    return chain
  }, [page, pages, project])

  const breadcrumb = useMemo(() => {
    if (!project || !page) return null
    return (
      <>
        <Link
          to="/$workspaceSlug/projects/$projectKey"
          params={{ workspaceSlug, projectKey }}
          className="flex items-center gap-1 hover:text-foreground transition-colors truncate"
        >
          <ProjectIcon iconType={project.iconType} icon={project.icon} iconColor={project.iconColor} size={14} />
          <span className="truncate">{project.name}</span>
        </Link>
        <CollapsibleAncestorBreadcrumbs
          ancestors={ancestors}
          projectId={project.id}
          pages={pages}
          renderLink={(ancestor) => (
            <Link
              to="/$workspaceSlug/projects/$projectKey/pages/$pageSlug"
              params={{ workspaceSlug, projectKey, pageSlug: ancestor.slug }}
              className="hover:text-foreground transition-colors truncate"
            >
              {ancestor.title || 'Untitled'}
            </Link>
          )}
          onNavigate={(slug) => navigate({
            to: '/$workspaceSlug/projects/$projectKey/pages/$pageSlug',
            params: { workspaceSlug, projectKey, pageSlug: slug },
          })}
        />
        <ChevronRight className="size-2.5 flex-shrink-0" />
        <span className="flex items-center gap-1.5 truncate">
          <span className="truncate">{page.title || 'Untitled'}</span>
          <StarToggle entityType="page" entityId={page.id} size={14} />
          <BreadcrumbSubPagesDropdown
            currentPageId={page.id}
            projectId={project.id}
            pages={pages}
            onNavigate={(slug) => navigate({
              to: '/$workspaceSlug/projects/$projectKey/pages/$pageSlug',
              params: { workspaceSlug, projectKey, pageSlug: slug },
            })}
          />
        </span>
      </>
    )
  }, [project, page, ancestors, workspaceSlug, projectKey, pages, navigate])

  // The same sidebar as everywhere else; its peek opens this project's docs (design 32b).
  const sidebar = useMemo(() => createElement(TaskSidebar), [])

  const handleNavigateToSubPage = useCallback((slug: string) => {
    navigate({
      to: '/$workspaceSlug/projects/$projectKey/pages/$pageSlug',
      params: { workspaceSlug, projectKey, pageSlug: slug },
    })
  }, [navigate, workspaceSlug, projectKey])

  const handleNavigateAfterDelete = useCallback((parentPage: PageDocument | null) => {
    if (parentPage) {
      navigate({
        to: '/$workspaceSlug/projects/$projectKey/pages/$pageSlug',
        params: { workspaceSlug, projectKey, pageSlug: parentPage.slug },
      })
      return
    }
    navigate({
      to: '/$workspaceSlug/projects/$projectKey',
      params: { workspaceSlug, projectKey },
    })
  }, [navigate, workspaceSlug, projectKey])

  if (pagesLoading) {
    return (
      <div className="flex min-h-full flex-1 items-center justify-center">
        <Loading />
      </div>
    )
  }

  if (!project || !page || !workspaceId) {
    return (
      <div className="flex min-h-full flex-1 items-center justify-center px-4">
        <div className="text-center text-muted-foreground">
          Page not found or may have been removed.
        </div>
      </div>
    )
  }

  return (
    <PageDetailsView
      page={page}
      project={project}
      pages={pages}
      workspaceId={workspaceId}
      userId={userId}
      pageSlug={pageSlug}
      sidebar={sidebar}
      breadcrumb={breadcrumb}
      layoutDeps={[project.id, project.name, project.icon, project.iconType, project.iconColor, projectKey, workspaceSlug, pageSlug, page.id, page.title, page.parentPageId, ancestors.length]}
      onNavigateToSubPage={handleNavigateToSubPage}
      onNavigateAfterDelete={handleNavigateAfterDelete}
    />
  )
}
